import { Injectable, inject } from '@angular/core';
import { DiskService } from '../../services/disk/disk.service';
import { DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import type { ConversationOrigin } from '../features/assistant-attachment';

/** One row in the local conversation index. */
export interface NovaConversationEntry {
  /** Foundry conversation id. */
  id: string;
  /** Where it was born (provenance only; context may range mid-thread). null = general. */
  origin: ConversationOrigin | null;
  /** Display title (origin label, or first user message). */
  title: string;
  /** Last message preview. */
  snippet: string;
  /** Last-activity ISO timestamp. */
  lastAt: string;
}

/**
 * Local index of assistant conversations, persisted as a single PouchDB doc
 * (`nova_conversations`). Makes conversations listable ("see all") and findable
 * by origin ("the conversation from this meeting"). Newest-first ordering is a
 * read-time sort on `lastAt`. Capped to the most-recent MAX entries, the
 * conversations the history lists.
 */
@Injectable({ providedIn: 'root' })
export class NovaConversationIndexService {
  readonly #disk = inject(DiskService);
  static readonly MAX = 50;

  /** All entries, newest first. */
  async list(): Promise<NovaConversationEntry[]> {
    const all = await this.#read();
    return [...all].sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? ''));
  }

  /** The most-recent entry whose origin matches, or null. */
  async findByOrigin(origin: ConversationOrigin): Promise<NovaConversationEntry | null> {
    const all = await this.list();
    return all.find(e =>
      e.origin && e.origin.kind === origin.kind && e.origin.id === origin.id) ?? null;
  }

  /**
   * Insert or update an entry by conversation id. Merges provided fields over
   * any existing row; trims the index to MAX newest afterwards.
   */
  async upsert(entry: Partial<NovaConversationEntry> & { id: string }): Promise<void> {
    const all = await this.#read();
    const idx = all.findIndex(e => e.id === entry.id);
    const merged: NovaConversationEntry = {
      id: entry.id,
      origin: entry.origin ?? (idx >= 0 ? all[idx].origin : null),
      title: entry.title ?? (idx >= 0 ? all[idx].title : 'New conversation'),
      snippet: entry.snippet ?? (idx >= 0 ? all[idx].snippet : ''),
      lastAt: entry.lastAt ?? new Date().toISOString(),
    };
    if (idx >= 0) all[idx] = merged; else all.push(merged);
    const trimmed = [...all]
      .sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? ''))
      .slice(0, NovaConversationIndexService.MAX);
    await this.#write(trimmed);
  }

  /**
   * Brings the index up to date with the conversations the host keeps for the rep: adds each of `hosted` not
   * already indexed, removes every entry whose id is in `deletedIds`, and trims the index to MAX newest. An entry
   * already in the index keeps its own fields, which hold the latest snippet and activity time on this device.
   */
  async mergeHosted(hosted: NovaConversationEntry[], deletedIds: string[]): Promise<void> {
    const deleted = new Set(deletedIds);
    const kept = (await this.#read()).filter(e => !deleted.has(e.id));
    const indexed = new Set(kept.map(e => e.id));
    const added = hosted.filter(e => !deleted.has(e.id) && !indexed.has(e.id));
    const merged = [...kept, ...added]
      .sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? ''))
      .slice(0, NovaConversationIndexService.MAX);
    await this.#write(merged);
  }

  /** Remove an entry (e.g. after a dead-conversation cleanup). */
  async remove(id: string): Promise<void> {
    const all = await this.#read();
    await this.#write(all.filter(e => e.id !== id));
  }

  async #read(): Promise<NovaConversationEntry[]> {
    try {
      const doc: any = await this.#disk.retrieve(DB_KEY_PREFIXES.NOVA_CONVERSATIONS, true);
      return Array.isArray(doc?.raw) ? (doc.raw as NovaConversationEntry[]) : [];
    } catch (e) {
      console.error('NovaConversationIndex read:', e);
      return [];
    }
  }

  async #write(entries: NovaConversationEntry[]): Promise<void> {
    try {
      await this.#disk.updateOrInsert(DB_KEY_PREFIXES.NOVA_CONVERSATIONS, () => ({ raw: entries }));
    } catch (e) {
      console.error('NovaConversationIndex write:', e);
    }
  }
}
